import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { IAccount, IAccountTag } from "./InvoiceTypes";
import { ErpType } from "src/network/User";
import { GET_ACCOUNT_DATA, GET_ACCOUNT_TAG_DATA } from "src/api/cred";
import { GetAccountData, GetAccountDataVariables } from "src/api/generated/GetAccountData";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GetAccountTagData, GetAccountTagDataVariables } from "src/api/generated/GetAccountTagData";
import { INVOICE_ACCOUNT_UNIT_OF_MEASURE, INVOICE_ACCOUNT_VAT_CONFIG } from "./InvoiceEnums";

export enum LOADING_ACCOUNT_DATA_STATE {
    INIT,
    LOADING
}

export const MAX_ACCOUNT_TAGS_TO_SHOW = 2;

export class AccountingAccountSearchStore {
    rootStore: RootStore;

    erpType: ErpType = ErpType.IT2;

    accountData: IAccount[] = [];
    accountTagData: Array<IAccountTag[]> = [];
    displayAllAccountTags: boolean[] = [];
    loadingAccountDataState: LOADING_ACCOUNT_DATA_STATE = LOADING_ACCOUNT_DATA_STATE.INIT;
    loadedAccountDataWithVariables: { realestateId: string; bookingDate: string } | undefined = undefined;
    loadedAccountTagDataWithVariables:
        | { creditorId: string; realestateId: string; bookingDate: string; accountingRowIndex: number }
        | undefined = undefined;

    // This is an array because every Accounting Row has its own account value
    currentAccountSearchQuery: string[] = [];
    selectedAccountQueryString: string[] = [];
    selectedAccountName: string[] = [];

    triggerFocusNextInput: { isActive: boolean; index: number } = { isActive: false, index: 0 };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            accountData: observable,
            accountTagData: observable,
            currentAccountSearchQuery: observable,
            selectedAccountQueryString: observable,
            selectedAccountName: observable,
            triggerFocusNextInput: observable,
            loadingAccountDataState: observable,
            loadedAccountDataWithVariables: observable,
            loadedAccountTagDataWithVariables: observable,
            displayAllAccountTags: observable,

            init: action,
            reset: action,
            resetAccountingRow: action,
            loadAccountData: action,
            loadAccountTagData: action,
            setLoadingAccountDataState: action,
            setCurrentAccountSearchQuery: action,
            setSelectedAccountQueryString: action,
            setSelectedAccountName: action,
            setTriggerFocusNextInput: action,
            filteredAccountData: action,
            setAccountData: action,
            setAccountTagData: action,
            getAllAccountTags: action,
            getAccountTagsCount: action,
            getAccountById: action,
            setDisplayAllAccountTags: action,

            displayedAccountTagsByIndex: computed,
            areAccountTagsDisplayedByIndex: computed
        });
    }

    init = () => {};

    reset = () => {
        this.currentAccountSearchQuery = [];
        this.selectedAccountQueryString = [];
        this.selectedAccountName = [];
        this.accountTagData = [];
    };

    resetAccountingRow = (accountingRowIndex: number) => {
        this.currentAccountSearchQuery[accountingRowIndex] = "";
        this.selectedAccountQueryString[accountingRowIndex] = "";
        this.selectedAccountName[accountingRowIndex] = "";
        this.accountTagData[accountingRowIndex] = [];
    };

    loadAccountData = async (accountingRowIndex: number) => {
        this.setLoadingAccountDataState(LOADING_ACCOUNT_DATA_STATE.LOADING);

        const currentInvoice = this.rootStore.invoiceStore.currentInvoice;
        const realestateId = currentInvoice?.getAccountingRealestate(accountingRowIndex)?.id;
        const date = currentInvoice?.getAccountingDate(accountingRowIndex);

        const hasAlreadyLoadedAccountData =
            this.loadedAccountDataWithVariables?.realestateId === realestateId &&
            this.loadedAccountDataWithVariables?.bookingDate === date;

        if (realestateId && date && !hasAlreadyLoadedAccountData) {
            this.loadedAccountDataWithVariables = {
                realestateId: realestateId,
                bookingDate: date
            };

            const { data }: { data: GetAccountData } = await apolloClientInstance.query<
                GetAccountData,
                GetAccountDataVariables
            >({
                query: GET_ACCOUNT_DATA,
                variables: {
                    realestateid: realestateId,
                    bookingdate: date
                }
            });

            const accountData: IAccount[] = data.accounts
                .map((account) => {
                    return {
                        id: account.id,
                        number: account.number,
                        name: account.name,
                        requiresCostCenter: account.requirescostcenter,
                        requiresQuantity: account.requiresquantitiy,
                        unitOfMeasure: account.unitofmeasure ?? INVOICE_ACCOUNT_UNIT_OF_MEASURE.ZERO,
                        extraCostDate: account.extracostdate,
                        vatConfig: account.vatconfig ?? INVOICE_ACCOUNT_VAT_CONFIG.WITHOUT_VAT,
                        defaultVatCodeId: account.defaultvatcodeid ?? undefined,
                        validTo: account.validto,
                        validFrom: account.validfrom
                    };
                })
                .sort((a, b) => {
                    // The result set comes already sorted by 'type'
                    // Then also sort by "number" (lexicographic or numeric comparison)
                    const numberComparison = a.number.localeCompare(b.number, undefined, {
                        numeric: true,
                        sensitivity: "base"
                    });
                    if (numberComparison !== 0) {
                        return numberComparison;
                    }
                    // Finally by "name" (string comparison)
                    return a.name.localeCompare(b.name);
                });

            this.setAccountData(accountData);
        }

        this.setLoadingAccountDataState(LOADING_ACCOUNT_DATA_STATE.INIT);
    };

    loadAccountTagData = async (
        creditorId: string,
        realestateId: string,
        bookingDate: string,
        accountingRowIndex: number
    ) => {
        const hasAlreadyLoadedAccountTagData =
            this.loadedAccountTagDataWithVariables?.realestateId === realestateId &&
            this.loadedAccountTagDataWithVariables?.bookingDate === bookingDate &&
            this.loadedAccountTagDataWithVariables?.creditorId === creditorId &&
            this.loadedAccountTagDataWithVariables?.accountingRowIndex === accountingRowIndex;

        if (
            !hasAlreadyLoadedAccountTagData ||
            (this.accountTagData[accountingRowIndex] && this.accountTagData[accountingRowIndex].length === 0)
        ) {
            this.loadedAccountTagDataWithVariables = {
                creditorId: creditorId,
                realestateId: realestateId,
                bookingDate: bookingDate,
                accountingRowIndex: accountingRowIndex
            };

            const { data }: { data: GetAccountTagData } = await apolloClientInstance.query<
                GetAccountTagData,
                GetAccountTagDataVariables
            >({
                query: GET_ACCOUNT_TAG_DATA,
                variables: {
                    creditorId: creditorId,
                    realestateId: realestateId,
                    bookingDate: bookingDate
                }
            });

            const accountTagData: IAccountTag[] = data.accounts_history.map((accountHistoryEntry) => {
                return {
                    accountId: accountHistoryEntry.account_id,
                    number: accountHistoryEntry.account_number ?? undefined,
                    name: accountHistoryEntry.account_name ?? undefined,
                    accountingsHistoryText: accountHistoryEntry.accountings_history_text ?? undefined,
                    numberOfRecords: accountHistoryEntry.number_of_records ?? undefined
                };
            });

            this.setAccountTagData(accountTagData, accountingRowIndex);
        }
    };

    // Getters
    getFormattedAccountFieldValue = (account: IAccount): string => {
        return `${account.number} ${account.name}`;
    };

    getCurrentAccountSearchQuery(accountingRowIndex: number) {
        return this.currentAccountSearchQuery[accountingRowIndex];
    }

    getSelectedAccountQueryString(accountingRowIndex: number) {
        return this.selectedAccountQueryString[accountingRowIndex];
    }

    getSelectedAccountName(accountingRowIndex: number) {
        return this.selectedAccountName[accountingRowIndex];
    }

    getAccountById = async (accountId: string, accountingRowIndex: number): Promise<IAccount | undefined> => {
        if (this.accountData.length === 0) {
            await this.loadAccountData(accountingRowIndex);
        }
        return this.accountData.find((account) => account.id === accountId);
    };

    // Setters
    setLoadingAccountDataState(loadingAccountDataState: LOADING_ACCOUNT_DATA_STATE) {
        this.loadingAccountDataState = loadingAccountDataState;
    }

    setAccountData(data: IAccount[]) {
        this.accountData = data;
    }

    setAccountTagData(accountTagData: IAccountTag[], accountingRowIndex: number) {
        this.accountTagData[accountingRowIndex] = accountTagData;
    }

    setCurrentAccountSearchQuery(currentAccountSearchQuery: string, accountingRowIndex: number) {
        this.currentAccountSearchQuery[accountingRowIndex] = currentAccountSearchQuery;
    }

    setSelectedAccountQueryString(selectedAccountQueryString: string, accountingRowIndex: number) {
        this.selectedAccountQueryString[accountingRowIndex] = selectedAccountQueryString;
    }

    setSelectedAccountName(selectedAccountName: string, accountingRowIndex: number) {
        this.selectedAccountName[accountingRowIndex] = selectedAccountName;
    }

    setDisplayAllAccountTags(displayAllAccountTags: boolean, accountingRowIndex: number) {
        this.displayAllAccountTags[accountingRowIndex] = displayAllAccountTags;
    }

    /*  This function triggers the focus on the next field after selecting an account
        For IT2 this will be the CostCenter field in the current accounting row
        For RIMO this will be the Booking Text field in the current accounting row
    */
    setTriggerFocusNextInput(triggerFocusNextInput: boolean, accountingRowIndex: number) {
        this.triggerFocusNextInput = {
            isActive: triggerFocusNextInput,
            index: accountingRowIndex
        };
    }

    filteredAccountData(accountingRowIndex: number): IAccount[] {
        let currentAccountSearchQuery = this.currentAccountSearchQuery[accountingRowIndex] ?? "";
        let selectedRealestateQueryString = this.selectedAccountQueryString[accountingRowIndex] ?? "";

        if (currentAccountSearchQuery === selectedRealestateQueryString) {
            return this.accountData;
        }

        if (currentAccountSearchQuery === "") {
            return this.accountData;
        }

        const filteredAccountData = this.accountData.filter((account) => {
            const accountName = `${account.number} ${account.name}`;

            return accountName.toLowerCase().includes(currentAccountSearchQuery.toLowerCase());
        });

        return filteredAccountData;
    }

    get areAccountTagsDisplayedByIndex(): boolean[] {
        const currentInvoice = this.rootStore.invoiceStore.currentInvoice;

        return this.accountTagData.map((_, index) => {
            return (
                !!currentInvoice && // Ensure currentInvoice is truthy (returns false if it's null or undefined)
                !currentInvoice.isAccountingAccountSelected(index) &&
                this.displayedAccountTagsByIndex[index] &&
                this.displayedAccountTagsByIndex[index].length > 0
            );
        });
    }

    getAllAccountTags = (accountingRowIndex: number): IAccountTag[] => {
        if (this.accountTagData[accountingRowIndex]) {
            return this.accountTagData[accountingRowIndex];
        } else {
            return [];
        }
    };

    hasMoreAccountTagsToDisplay = (accountingRowIndex: number): boolean => {
        return (
            this.getAccountTagsCount(accountingRowIndex) > MAX_ACCOUNT_TAGS_TO_SHOW &&
            (this.displayAllAccountTags[accountingRowIndex] === undefined ||
                this.displayAllAccountTags[accountingRowIndex] === false)
        );
    };

    getAccountTagsCount = (accountingRowIndex: number): number => {
        if (this.accountTagData[accountingRowIndex]) {
            return this.accountTagData[accountingRowIndex].length;
        } else {
            return 0;
        }
    };

    get displayedAccountTagsByIndex(): IAccountTag[][] {
        return this.accountTagData.map((accountTags, index) => {
            const allAccountTagsAreDisplayed = this.displayAllAccountTags[index] === true;
            if (allAccountTagsAreDisplayed || this.getAccountTagsCount(index) <= MAX_ACCOUNT_TAGS_TO_SHOW) {
                return accountTags;
            } else if (this.hasMoreAccountTagsToDisplay(index)) {
                return accountTags.slice(0, MAX_ACCOUNT_TAGS_TO_SHOW);
            }
            return [];
        });
    }
}
